/* Copyright (c) 2017 Backpack Health LLC */

import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './Icons.scss';

const IconSizeShape = PropTypes.oneOf(['default', 'extraSmall', 'small', 'medium', 'large', 'nav']);

const IconSizeNames = {
  default: 'default',
};

const IconBoundSizes = {
  default: {},
};


function generateFontAwesomeIcon(iconName, fontSize, bounds, lineHeight) {
  const FAIcon = (props) => {
    const classes = classnames(
      'fa',
      `fa-${iconName}`,
      `size-${props.size}`,
      {
        'is-clickable': !!props.onClick,
        'font-color-input-error': props.color === 'error',
        'font-color-input-active': props.color === 'active',
        'font-color-input-inactive': props.color === 'inactive',
        'font-color-input-white': props.color === 'white',
        'font-color-warning': props.color === 'warning',
        'font-color-profile': props.color === 'profile',
        'font-color-middle-gray': props.color === 'middle-gray',
        'font-color-dark-gray': props.color === 'darkGray',
        'font-color-meddark-gray': props.color === 'medDarkGray',
      }
    );
    const styles = _.assign({}, {
      lineHeight: props.lineHeight ? props.lineHeight : lineHeight,
      width: bounds,
      height: bounds,
      fontSize,
    });
    return <div className="cmp-icon" onClick={props.onClick}><i style={styles} className={classes} /></div>;
  };
  FAIcon.propTypes = {
    color: PropTypes.oneOf(['white', 'default', 'error', 'light', 'inactive', 'warning', 'profile', 'medDarkGray', 'middle-gray', 'darkGray']),
    lineHeight: PropTypes.string,
    size: IconSizeShape,
    onClick: PropTypes.func,
  };
  FAIcon.defaultProps = {
    size: IconSizeNames.default,
  };
  return FAIcon;
}

function generateMaterialIcon(materialName, fontSize, bounds, lineHeight) {
  const MaterialIcon = (props) => {
    const boundSize = props.useSmallerBounds ? IconBoundSizes[props.size] : {};
    const size = props.useSmallerBounds ? boundSize.fontSize : props.size;

    const classes = classnames(
      'material-icons',
      `size-${size}`,
      {
        'is-clickable': props.isClickable,
        'font-color-input-error': props.color === 'error',
        'font-color-input-active': props.color === 'active',
        'font-color-input-inactive': props.color === 'inactive',
        'font-color-light': props.color === 'light',
        'font-color-warning': props.color === 'warning',
        'font-color-input-white': props.color === 'white',
        'font-color-middle-gray': props.color === 'middle-gray',
        'font-color-dark-gray': props.color === 'darkGray',
        'font-color-meddark-gray': props.color === 'medDarkGray',
        'font-color-profile': props.color === 'profile',
      }
    );
    const styles = _.assign({}, boundSize, {
      lineHeight: props.lineHeight ? props.lineHeight : lineHeight,
      fontSize,
      width: bounds,
      height: bounds,
    });

    return <i style={styles} className={classes}>{materialName}</i>;
  };
  MaterialIcon.propTypes = {
    color: PropTypes.oneOf(['white', 'default', 'error', 'light', 'inactive', 'warning', 'active', 'profile', 'medDarkGray', 'middle-gray', 'darkGray']),
    size: IconSizeShape,
    isClickable: PropTypes.bool,
    lineHeight: PropTypes.string,
    useSmallerBounds: PropTypes.bool,
  };
  MaterialIcon.defaultProps = {
    size: IconSizeNames.default,
  };
  return MaterialIcon;
}

const IconWithText = (props) => {
  const classes = classnames(
    'cmp-icon-with-text',
    {
      'is-disabled': props.isDisabled,
      'is-clickable': _.isFunction(props.onClick),
      'font-color-input-error': props.color === 'error',
      'font-color-input-active': props.color === 'active',
      'font-color-input-inactive': props.color === 'inactive',
      'font-color-light': props.color === 'light',
    }
  );
  return (
    <div className={classes} onClick={props.onClick}>
      {
        props.leftText ? (
          <span className="left-side">
            {props.leftText}
          </span>
        ) : null
      }
      <span>
        {props.icon}
      </span>
      {
        props.rightText ? (
          <span className="right-side">
            {props.rightText}
          </span>
        ) : null
      }
    </div>
  );
};
IconWithText.propTypes = {
  color: PropTypes.oneOf(['default', 'error', 'light', 'active', 'inactive']),
  leftText: PropTypes.any,
  rightText: PropTypes.any,
  icon: PropTypes.element,
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool,
};

export {
  generateFontAwesomeIcon,
  generateMaterialIcon,
  IconWithText,
};

